import React, {
    ChangeEvent,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react';
import './ReviewDocumentForm.scss';
import { Form, Input } from '@availity/form';
import { Col, InputGroup, InputGroupAddon } from 'reactstrap';
import * as yup from 'yup';
import { useStateSelector } from '../../../../store/selectors';
import createNotification from '../../../../utils/createNotification';
import Helpers from '../../../../utils/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { EngagementDocumentSource } from '../../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import {
    createDefaultDocumentDetails,
    DocumentDetails,
} from '../../../../slices/marketplace/timeline/models/document-details.model';
import { ActionDocumentType } from '../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import { DocumentDetailsStatus } from '../../../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineActionType } from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import {
    TimelineAction,
    defaultTimelineAction,
} from '../../../../slices/marketplace/timeline/models/timeline-actions.model';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { MarketplaceDocumentSectionField } from '../DocumentSectionField/MarketplaceDocumentSectionField';
import { DocumentSection } from '../../../../slices/marketplace/timeline/models/document-section.model';
import { MarketplaceDocumentSectionFieldValidator } from '../DocumentSectionField/MarketplaceDocumentSectionFieldValidator';
import { generateGuid } from '../../../../utils/typedHelper';

interface ReviewDocumentFormProps {
    documentDetails?: DocumentDetails;
    onSubmit(action: TimelineAction, documentDetails?: DocumentDetails): void;
    disabled: boolean;
    downloadFile(): void;
    currentEvent: TimelineEvent;
    timelineEvents: TimelineEvent[];
    documentDetailsList: DocumentDetails[];
    documentsSections: DocumentSection[];
    setDocumentSectionList(sections: DocumentSection[]): void;
}

const ReviewDocumentForm = forwardRef((props: ReviewDocumentFormProps, ref) => {
    const [attachedFile, setAttachedFile] = useState<File>(
        props.documentDetails?.localFile
    );
    const [documentDetails, setDocumentDetails] = useState(
        props.documentDetails ??
            createDefaultDocumentDetails(
                true,
                undefined,
                ActionDocumentType.Downloadable,
                undefined,
                undefined,
                TimelineParty.Buyer
            )
    );
    const [engagementDocumentsToReview, setEngagementDocumentsToReview] =
        useState<DocumentDetails[]>([]);
    const [isFileUpdated, setIsFileUpdated] = useState(false);
    const [selectedEngagementDocumentId, setSelectedEngagementDocumentId] =
        useState<string>('');

    const axios = useStateSelector((state) => state.core.axios);
    let modalForm: any = {};

    useEffect(() => {
        getDocumentsToReview();
    }, []);

    useEffect(() => {
        if (props.currentEvent?.linkedAction?.actionReferenceId) {
            setSelectedEngagementDocumentId(
                getDocumentIdByActionId(
                    props.currentEvent.linkedAction.actionReferenceId
                )
            );
        }
    }, [props.currentEvent]);

    const getDocumentsToReview = () => {
        const docsWithFeedback = props.documentDetailsList.filter(
            (doc) => doc.type === ActionDocumentType.DocumentWithFeedback
        );
        const validDocumentIds = new Set(docsWithFeedback.map((doc) => doc.id));
        const relevantEvents = props.timelineEvents.filter(
            (event) =>
                (event.dayCompleted ?? event.dayEnd) <=
                    props.currentEvent.dayStart &&
                event.linkedAction &&
                (event.linkedAction.referenceItemId != null ||
                    validDocumentIds.has(event.linkedAction.referenceItemId))
        );
        const documentsToReview = docsWithFeedback.filter((doc) =>
            relevantEvents.some(
                (event) => event.linkedAction?.referenceItemId === doc.id
            )
        );

        setEngagementDocumentsToReview(documentsToReview);
    };

    const getActionIdByDocumentId = (documentId: string): number | undefined =>
        props.timelineEvents.find(
            (e) => e.linkedAction?.referenceItemId === documentId
        )?.linkedAction.id;

    const getDocumentIdByActionId = (actionId: number): string | undefined =>
        props.timelineEvents.find((e) => e.linkedAction?.id === actionId)
            ?.linkedAction?.referenceItemId;

    const downloadNewSelectedFile = () => {
        if (attachedFile) {
            const fr = new FileReader();
            fr.readAsDataURL(attachedFile);
            const blob = new Blob([attachedFile], { type: attachedFile.type });
            const objectURL = window.URL.createObjectURL(blob);
            var link = document.createElement('a');
            link.href = objectURL;
            link.download = attachedFile.name;
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    useImperativeHandle(ref, () => ({
        handleSubmit() {
            modalForm?.handleSubmit();
        },
    }));

    const changeFileHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const newFile = event.target.files[0];
        if (newFile) {
            setIsFileUpdated(true);
            setAttachedFile(newFile);
            modalForm.setFieldValue('sourceFile', newFile.name, true);
            if (selectedEngagementDocumentId) {
                modalForm.setFieldValue('documentSection', '-1', true);
            }

            const newId = generateGuid();

            const newDocument: DocumentDetails = {
                ...documentDetails,
                id: newId,
                name: newFile.name,
                masterDocumentId: newId,
                localFile: newFile,
            };

            setDocumentDetails(newDocument);
            setSelectedEngagementDocumentId('');
        }
    };

    const uploadFile = async (file: File): Promise<DocumentDetails> => {
        const formData = new FormData();
        formData.append('File', file);
        const result = await axios
            .post(`/api/temporaryfiles`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
            .then((responses): DocumentDetails => {
                if (responses.status !== 200) {
                    createNotification(
                        'Some files are not uploaded due to an unexpected error.',
                        'error'
                    );
                } else {
                    return {
                        ...documentDetails,
                        temporaryFileId: responses.data.id,
                        source: EngagementDocumentSource.AdminUpload,
                    };
                }
            });
        return result;
    };

    return (
        <div className="review-document-form">
            <Form
                innerRef={(c) => {
                    modalForm = c;
                }}
                initialValues={{
                    actionType: TimelineActionType.BuyerReviewDocument,
                    sourceFile: props.documentDetails?.name ?? '',
                    engagementDocument: props.currentEvent?.linkedAction
                        ? getDocumentIdByActionId(
                              props.currentEvent?.linkedAction?.id
                          )
                        : '',
                    file: null,
                    documentSection: props.documentDetails?.sectionId ?? '',
                }}
                validationSchema={yup.object().shape({
                    engagementDocument: yup.string().nullable(),
                    sourceFile: yup.string().nullable(),
                    documentSection: yup.string().when('sourceFile', {
                        is: (val: string) => !!val,
                        then: () =>
                            MarketplaceDocumentSectionFieldValidator.documentSectionValidationRules(
                                props.documentsSections,
                                TimelineParty.Buyer
                            ),
                    }),
                })}
                onSubmit={async (values, formikHelpers) => {
                    const isFileSelected = !!attachedFile;
                    const isDocSelected = !!selectedEngagementDocumentId;

                    if (
                        !isFileSelected &&
                        !isDocSelected &&
                        !values.sourceFile
                    ) {
                        formikHelpers.setErrors({
                            engagementDocument:
                                'Select a document or upload a file',
                            sourceFile: 'Select a document or upload a file',
                        });
                        return;
                    }

                    if (isFileSelected && isDocSelected) {
                        formikHelpers.setErrors({
                            engagementDocument: 'Choose only one option',
                            sourceFile: 'Choose only one option',
                        });
                        return;
                    }

                    if (!values.sourceFile) {
                        const document = props.documentDetailsList.find(
                            (d) => d.id == selectedEngagementDocumentId
                        );
                        if (
                            props.documentsSections.every(
                                (d) =>
                                    d.id !== document.sectionId ||
                                    !d.isAvailableForBuyers
                            )
                        ) {
                            formikHelpers.setErrors({
                                documentSection:
                                    'Selected section is not available for buyers',
                            });
                            return;
                        }
                    }

                    let docDetails: DocumentDetails | undefined;
                    let actionReferenceId: number | undefined;

                    if (selectedEngagementDocumentId) {
                        actionReferenceId = getActionIdByDocumentId(
                            values.engagementDocument
                        );
                        docDetails = engagementDocumentsToReview.find(
                            (d) => d.id == selectedEngagementDocumentId
                        );
                    } else {
                        docDetails = isFileUpdated
                            ? await uploadFile(attachedFile)
                            : documentDetails;
                        docDetails.sectionId = values.documentSection;
                    }

                    props.onSubmit(
                        {
                            ...defaultTimelineAction,
                            actionType: values.actionType,
                            referenceItemId: docDetails?.id,
                            actionReferenceId,
                        },
                        docDetails
                    );
                }}>
                <Col>
                    <div className="header-container">
                        <div className="engagement-doc-dropdown">
                            <Input
                                type="select"
                                name="engagementDocument"
                                disabled={props.disabled}
                                value={selectedEngagementDocumentId}
                                onChange={(e) => {
                                    const selectedId = e.target.value;
                                    setSelectedEngagementDocumentId(selectedId);
                                    setAttachedFile(null);
                                    setIsFileUpdated(false);
                                    modalForm.setFieldValue(
                                        'sourceFile',
                                        '',
                                        true
                                    );
                                }}>
                                <option value="">
                                    Select an Engagement document
                                </option>
                                {engagementDocumentsToReview.map((doc) => (
                                    <option key={doc.id} value={doc.id}>
                                        {doc.name}
                                    </option>
                                ))}
                            </Input>
                            {selectedEngagementDocumentId ? (
                                <MarketplaceDocumentSectionField
                                    documentsSections={props.documentsSections}
                                    disabled={true}
                                    setDocumentSectionList={
                                        props.setDocumentSectionList
                                    }
                                    documentDetailsList={
                                        props.documentDetailsList
                                    }
                                    type={'buyers'}
                                    selectedDocumentId={
                                        selectedEngagementDocumentId
                                    }
                                    sectionsModalMode={
                                        'built-in'
                                    }></MarketplaceDocumentSectionField>
                            ) : (
                                []
                            )}
                        </div>
                        <div className="text-or">or</div>
                        <div
                            className={`${'select-file'} ${
                                props.disabled ? 'file-select-disabled' : ''
                            }`}>
                            <label
                                className="link-button"
                                htmlFor="upload-file"
                                aria-disabled={props.disabled}>
                                Select File
                            </label>
                            <input
                                hidden={true}
                                type="file"
                                name="file"
                                id="upload-file"
                                className="upload-file-input"
                                disabled={props.disabled}
                                onChange={changeFileHandler}
                            />
                        </div>
                    </div>

                    {(attachedFile != null || props.documentDetails != null) &&
                    (!selectedEngagementDocumentId ||
                        selectedEngagementDocumentId == '') ? (
                        <>
                            <InputGroup
                                className={`${
                                    props.disabled ? 'file-select-disabled' : ''
                                }`}>
                                <Input
                                    type="text"
                                    name="sourceFile"
                                    disabled={true}
                                    className="form-control file-display"
                                />
                                <InputGroupAddon addonType="append">
                                    <div className="input-group-text file-addon no-right-padding">
                                        <FontAwesomeIcon
                                            className="download-icon"
                                            icon={faDownload}
                                            onClick={() => {
                                                documentDetails.isUnregisteredFile
                                                    ? downloadNewSelectedFile()
                                                    : props.downloadFile();
                                            }}></FontAwesomeIcon>
                                    </div>
                                </InputGroupAddon>
                            </InputGroup>
                            <MarketplaceDocumentSectionField
                                documentsSections={props.documentsSections}
                                disabled={props.disabled}
                                setDocumentSectionList={
                                    props.setDocumentSectionList
                                }
                                documentDetailsList={props.documentDetailsList}
                                type={'buyers'}
                                sectionsModalMode={
                                    'built-in'
                                }></MarketplaceDocumentSectionField>
                        </>
                    ) : (
                        <></>
                    )}
                </Col>
            </Form>
        </div>
    );
});

export default ReviewDocumentForm;
